// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Sparkles, AlertTriangle } from 'lucide-react'
import { apiFetch } from '@/lib/api'
import { formatDisplayDateTime } from '@/lib/date-format'

// GET /v1/patients/{id}/snapshot — latest deterministic clinical projection fed to
// the intake AI. `content` is an indented XML document (xml.MarshalIndent server-side).
interface ApiSnapshot {
  version: number
  generatedAt: string
  resourceCount: number
  content: string
}

type SnapshotState = 'loading' | 'ready' | 'empty' | 'error'

// The snapshot document is already-indented XML — render it faithfully in Raw mode,
// only trimming surrounding blank lines.
function snapshotBody(content: string): string {
  return content.replace(/^\s*\n/, '').trimEnd()
}

interface SnapItem {
  text: string
  meta: string
}
interface SnapGroup {
  key: string
  title: string
  items: SnapItem[]
}

// parseSnapshot turns the indented XML projection into readable groups for the View
// mode. Returns null when the document can't be parsed (View falls back to Raw).
function parseSnapshot(xml: string): SnapGroup[] | null {
  try {
    const doc = new DOMParser().parseFromString(xml, 'application/xml')
    if (doc.querySelector('parsererror')) return null
    const root = doc.documentElement
    if (!root) return null
    const clean = (s: string | null) => (s ?? '').replace(/\s+/g, ' ').trim()
    const year = (iso: string | null) => {
      if (!iso) return ''
      const d = new Date(iso)
      return Number.isNaN(d.getTime()) ? '' : String(d.getFullYear())
    }
    const defs: { tag: string; title: string; meta: (el: Element) => string }[] = [
      { tag: 'problem', title: 'Problems', meta: el => year(el.getAttribute('onset')) },
      { tag: 'medication', title: 'Medications', meta: el => clean(el.getAttribute('dosage')) },
      { tag: 'allergy', title: 'Allergies', meta: el => (clean(el.getAttribute('reaction')) ? `Reaction: ${clean(el.getAttribute('reaction'))}` : '') },
      { tag: 'immunization', title: 'Immunizations', meta: el => year(el.getAttribute('date')) },
      { tag: 'procedure', title: 'Procedures', meta: el => year(el.getAttribute('date')) },
      { tag: 'encounter', title: 'Encounters', meta: () => '' },
    ]
    const groups: SnapGroup[] = []
    for (const d of defs) {
      const items = Array.from(root.getElementsByTagName(d.tag))
        .map(el => ({ text: clean(el.textContent), meta: d.meta(el) }))
        .filter(i => i.text)
      if (items.length) groups.push({ key: d.tag, title: d.title, items })
    }
    return groups.length ? groups : null
  } catch {
    return null
  }
}

// Snapshot tab — transparency/QA view of the AI patient snapshot. Fetches on mount
// (the tab only mounts when opened), so no clinical read happens until requested.
export function SnapshotView({ patientId }: { patientId: string }) {
  const { t } = useTranslation()
  const [state, setState] = useState<SnapshotState>('loading')
  const [snapshot, setSnapshot] = useState<ApiSnapshot | null>(null)
  const [mode, setMode] = useState<'view' | 'raw'>('view')
  const groups = useMemo(() => (snapshot ? parseSnapshot(snapshot.content) : null), [snapshot])

  useEffect(() => {
    const controller = new AbortController()
    setState('loading')
    setSnapshot(null)
    ;(async () => {
      try {
        const res = await apiFetch(`/v1/patients/${encodeURIComponent(patientId)}/snapshot`, { signal: controller.signal })
        if (res.status === 404) {
          setState('empty')
          return
        }
        if (!res.ok) throw new Error(`Server returned ${res.status}`)
        const data = (await res.json()) as ApiSnapshot
        setSnapshot(data)
        setState('ready')
      } catch {
        if (controller.signal.aborted) return
        setState('error')
      }
    })()
    return () => controller.abort()
  }, [patientId])

  if (state === 'loading') {
    return (
      <div className="flex items-center gap-2 py-16 justify-center text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('patientDetail.snapshotLoading')}
      </div>
    )
  }

  if (state === 'empty') {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <div className="h-12 w-12 rounded-xl bg-secondary flex items-center justify-center">
          <Sparkles className="h-6 w-6 text-muted-foreground" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">{t('patientDetail.snapshotEmpty')}</p>
          <p className="text-[13px] text-muted-foreground mt-1">{t('patientDetail.snapshotEmptyBody')}</p>
        </div>
      </div>
    )
  }

  if (state === 'error' || !snapshot) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2.5 text-[13px] text-destructive">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        <span>{t('patientDetail.snapshotError')}</span>
      </div>
    )
  }

  // View is the default; Raw is available for QA. Force Raw if the XML won't parse.
  const showRaw = mode === 'raw' || !groups

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <Sparkles className="h-4 w-4 text-accent shrink-0" />
          <p className="text-[13px] text-muted-foreground truncate">
            {t('patientDetail.snapshotMeta', {
              version: snapshot.version,
              count: snapshot.resourceCount,
              date: formatDisplayDateTime(snapshot.generatedAt),
            })}
          </p>
        </div>
        {groups && (
          <div className="flex shrink-0 rounded-lg border border-border overflow-hidden text-[12px] font-medium">
            {(['view', 'raw'] as const).map(m => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={
                  (mode === m ? 'bg-accent text-accent-foreground' : 'bg-card text-muted-foreground hover:text-foreground') +
                  ' px-3 py-1 capitalize transition-colors'
                }
              >
                {m}
              </button>
            ))}
          </div>
        )}
      </div>

      {showRaw ? (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <pre className="p-4 font-mono text-[12px] leading-relaxed text-foreground whitespace-pre">{snapshotBody(snapshot.content)}</pre>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {groups!.map(g => (
            <section key={g.key} className="bg-card border border-border rounded-xl overflow-hidden">
              <header className="flex items-baseline justify-between gap-2 px-4 py-2.5 border-b border-border bg-secondary/40">
                <h3 className="text-[13px] font-semibold text-foreground">{g.title}</h3>
                <span className="text-[11px] tabular-nums text-muted-foreground">{g.items.length}</span>
              </header>
              <ul className="divide-y divide-border/60">
                {g.items.map((it, i) => (
                  <li key={i} className="flex items-baseline justify-between gap-3 px-4 py-2">
                    <span className="text-[13px] text-foreground leading-snug">{it.text}</span>
                    {it.meta && <span className="text-[11px] text-muted-foreground whitespace-nowrap shrink-0">{it.meta}</span>}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}
